import i18n from '@/i18n';

export interface Device {
  id: number;
  name: string;
  uniqueId: string;
  phone?: string;
  status: "online" | "offline" | "unknown";
  disabled: boolean;
  lastUpdate: string;
  positionId: number;
  groupId: number;
  category: string;
  model: string;
  attributes?: Record<string, string | number | boolean>;
}

export interface Position {
  id: number;
  deviceId: number;
  protocol: string;
  serverTime: string;
  deviceTime: string;
  fixTime: string;
  outdated: boolean;
  valid: boolean;
  latitude: number;
  longitude: number;
  altitude: number;
  speed: number;
  course: number;
  address: string;
  attributes: Record<string, unknown>;
}

export interface Event {
  id: number;
  type: string;
  eventTime: string;
  deviceId: number;
  positionId: number;
  attributes: Record<string, unknown>;
}

export interface Summary {
  deviceId: number;
  deviceName: string;
  distance: number;
  averageSpeed: number;
  maxSpeed: number;
  engineHours: number;
  spentFuel: number;
}

export interface Group {
  id: number;
  name: string;
  groupId?: number;
}

export interface User {
  id: number;
  name: string;
  email: string;
  administrator: boolean;
  deviceReadonly?: boolean;
  deviceLimit: number;
  userLimit: number;
  attributes?: Record<string, unknown>;
}

export const EDGE_FUNCTION_URL = import.meta.env.VITE_TRACCAR_URL || '';
const MAX_ERROR_LENGTH = 500

const callApi = async (endpoint: string, params?: Record<string, string | string[]>) => {
  const base = EDGE_FUNCTION_URL || window.location.origin;
  const url = new URL(`/api/${endpoint}`, base);
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.forEach((v) => url.searchParams.append(key, v));
      } else {
        url.searchParams.append(key, value);
      }
    });
  }

  const response = await fetch(url.toString(), {
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || "Failed to call API");
  }

  return data;
};

export const loginToTraccar = async (email: string, password: string): Promise<Response> => {
    const formData = new URLSearchParams();
    formData.append("email", email);
    formData.append("password", password);

    return fetch(`${EDGE_FUNCTION_URL}/api/session`, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
      body: formData.toString(),
    });
};
export const exchangeTokenForSession = async (token: string): Promise<Response> => {
    return fetch(`${EDGE_FUNCTION_URL}/api/session?token=${encodeURIComponent(token)}`);
};

export const fetchDevices = async (): Promise<Device[]> => {
  return callApi("devices");
};

export const fetchPositions = async (): Promise<Position[]> => {
  return callApi("positions");
};

export const fetchDevicePosition = async (deviceId: number): Promise<Position | null> => {
  const positions: Position[] = await callApi("positions", { deviceId: deviceId.toString() });
  return positions[0] ?? null;
};

export const fetchPositionsByIds = async (ids: number[]): Promise<Position[]> => {
  if (ids.length === 0) return [];
  return callApi("positions", { id: ids.map(String) });
};

export const fetchGroups = async (): Promise<Group[]> => {
  return callApi("groups");
};

export const createGroup = async (group: { name: string; groupId?: number }): Promise<Group> => {
  const response = await fetch(`${EDGE_FUNCTION_URL}/api/groups`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
    body: JSON.stringify(group),
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to create group');
  return data;
};

export const updateGroup = async (group: Group): Promise<Group> => {
  const { id, name, groupId, attributes } = group as Group & { attributes?: Record<string, unknown> };
  const response = await fetch(`${EDGE_FUNCTION_URL}/api/groups/${group.id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
    body: JSON.stringify({ id, name, groupId: groupId ?? 0, attributes: attributes ?? {} }),
  });
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.error || 'Failed to update group');
  }
  return response.json();
};

export const deleteGroup = async (groupId: number): Promise<void> => {
  const response = await fetch(`${EDGE_FUNCTION_URL}/api/groups/${groupId}`, {
    method: 'DELETE',
    headers: { 'Accept': 'application/json' },
  });
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.error || 'Failed to delete group');
  }
};

export const fetchEvents = async (deviceId?: number, from?: string, to?: string): Promise<Event[]> => {
  const params: Record<string, string> = {};
  if (deviceId) params.deviceId = deviceId.toString();
  if (from) params.from = from;
  if (to) params.to = to;

  return callApi("reports/events", params);
};

export const fetchSummary = async (deviceIds: number[], from: string, to: string): Promise<Summary[]> => {
  return callApi("reports/summary", {
    deviceId: deviceIds.map((id) => id.toString()),
    from,
    to,
  });
};

export const fetchRoute = async (deviceId: number, from: string, to: string): Promise<Position[]> => {
  return callApi("reports/route", {
    deviceId: deviceId.toString(),
    from,
    to,
  });
};

export const fetchTrips = async (deviceId: number, from: string, to: string): Promise<Trip[]> => {
  return callApi("reports/trips", {
    deviceId: deviceId.toString(),
    from,
    to,
  });
};

export interface Trip {
  deviceId: number;
  deviceName: string;
  distance: number;
  averageSpeed: number;
  maxSpeed: number;
  duration: number;
  startTime: string;
  endTime: string;
  startAddress: string;
  endAddress: string;
  startLat: number;
  startLon: number;
  endLat: number;
  endLon: number;
  driverUniqueId?: string;
  driverName?: string;
  startOdometer: number;
  endOdometer: number;
  startHours: number;
  endHours: number;
}

export interface Stop {
  deviceId: number;
  deviceName: string;
  driverName?: string;
  duration: number;
  startTime: string;
  endTime: string;
  address: string;
  latitude: number;
  longitude: number;
  engineHours: number;
}

export const fetchStops = async (deviceId: number, from: string, to: string): Promise<Stop[]> => {
  return callApi("reports/stops", {
    deviceId: deviceId.toString(),
    from,
    to,
  });
};

export interface Geofence {
  id: number;
  name: string;
  description?: string;
  area: string; // WKT format: CIRCLE, POLYGON, LINESTRING
  calendarId?: number;
  attributes: Record<string, unknown>;
}

export const fetchSession = async (): Promise<User> => {
  const res = await fetch(`${EDGE_FUNCTION_URL}/api/session`);
  if (!res.ok) throw new Error('Not authenticated');
  return res.json();
};

export const fetchGeofences = async (): Promise<Geofence[]> => {
  return callApi("geofences");
};

export const fetchUsers = async (): Promise<User[]> => {
  return callApi("users");
};

export const createDevice = async (device: { name: string; uniqueId: string; groupId?: number; category?: string; attributes?: Record<string, string | number | boolean> }): Promise<Device> => {
  const response = await fetch(`${EDGE_FUNCTION_URL}/api/devices`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    },
    body: JSON.stringify(device),
  });

  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to create device');
  return data;
};

export const updateDevice = async (device: Device): Promise<Device> => {
  const response = await fetch(`${EDGE_FUNCTION_URL}/api/devices/${device.id}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    },
    body: JSON.stringify(device),
  });

  if (!response.ok) {
    const text = await response.text();
    if (text.includes('java.lang.SecurityException')) throw new Error(i18n.t('common.permissionDenied'));
    let message = text;
    try { message = JSON.parse(text)?.error || text; } catch { /* plain text */ }
    throw new Error((message || 'Failed to update device').slice(0, MAX_ERROR_LENGTH));
  }
  return response.json();
};

export const updateDeviceAccumulators = async (deviceId: number, totalDistance: number, hours: number): Promise<void> => {
  const response = await fetch(`${EDGE_FUNCTION_URL}/api/devices/${deviceId}/accumulators`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    },
    body: JSON.stringify({ deviceId, totalDistance, hours }),
  });
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.error || i18n.t('devices.updateAccumulatorsError'));
  }
};

export const deleteDevice = async (deviceId: number): Promise<void> => {
  const response = await fetch(`${EDGE_FUNCTION_URL}/api/devices/${deviceId}`, {
    method: 'DELETE',
    headers: {
      'Accept': 'application/json',
    },
  });
  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.error || 'Failed to delete device');
  }
};

export const deleteUser = async (userId: number): Promise<void> => {
  const response = await fetch(`${EDGE_FUNCTION_URL}/api/users/${userId}`, {
    method: 'DELETE',
    headers: {
      'Accept': 'application/json',
    },
  });

  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.error || 'Failed to delete user');
  }
};

export const loginAsUser = async (userId: number): Promise<Response> => {
  return fetch(`${EDGE_FUNCTION_URL}/api/session/${userId}`, {
    headers: {
      'Accept': 'application/json',
    },
  });
};

export const createGeofence = async (geofence: Omit<Geofence, 'id'>): Promise<Geofence> => {
  const response = await fetch(`${EDGE_FUNCTION_URL}/api/geofences`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    },
    body: JSON.stringify(geofence),
  });

  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to create geofence');
  return data;
};

export const updateGeofence = async (geofence: Geofence): Promise<Geofence> => {
  const response = await fetch(`${EDGE_FUNCTION_URL}/api/geofences/${geofence.id}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    },
    body: JSON.stringify(geofence),
  });

  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to update geofence');
  return data;
};

export const deleteGeofence = async (id: number): Promise<void> => {
  const response = await fetch(`${EDGE_FUNCTION_URL}/api/geofences/${id}`, {
    method: 'DELETE',
    headers: {
      'Accept': 'application/json',
    },
  });

  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.error || 'Failed to delete geofence');
  }
};

export interface CreateUserPayload {
  name: string;
  login: string;
  email: string;
  password: string;
  administrator?: boolean;
  attributes?: Record<string, unknown>;
}

export const updateUser = async (user: User & { password?: string }): Promise<User> => {
  const response = await fetch(`${EDGE_FUNCTION_URL}/api/users/${user.id}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    },
    body: JSON.stringify(user),
  });

  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to update user');
  return data;
};

export const createUser = async (user: CreateUserPayload): Promise<User> => {
  const response = await fetch(`${EDGE_FUNCTION_URL}/api/users`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    },
    body: JSON.stringify(user),
  });

  if (!response.ok) throw new Error(await response.text());
  return response.json();
};

export interface Permission {
  userId: number;
  deviceId?: number;
  groupId?: number;
  geofenceId?: number;
}

export interface PermissionsMap {
  devicesByUser: Record<number, number[]>;
  groupsByUser: Record<number, number[]>;
}

export const fetchPermissionsMap = async (): Promise<PermissionsMap> => {
  const url = `${import.meta.env.VITE_SUPABASE_URL || ''}/functions/v1/reseller-api/api/permissions`;
  const response = await fetch(url, {
    headers: { "Content-Type": "application/json", Accept: "application/json" },
  });
  if (!response.ok) throw new Error(await response.text());
  const data = await response.json() as {
    userDevice: Array<{ userid: number; deviceid: number }>;
    userGroup: Array<{ userid: number; groupid: number }>;
  };
  const devicesByUser: Record<number, number[]> = {};
  for (const { userid, deviceid } of data.userDevice) {
    (devicesByUser[userid] ??= []).push(deviceid);
  }
  const groupsByUser: Record<number, number[]> = {};
  for (const { userid, groupid } of data.userGroup) {
    (groupsByUser[userid] ??= []).push(groupid);
  }
  return { devicesByUser, groupsByUser };
};

export const userDevicesFromMap = (userId: number, allDevices: Device[], map: PermissionsMap): Device[] => {
  const directIds = new Set(map.devicesByUser[userId] ?? []);
  const groupIds = new Set(map.groupsByUser[userId] ?? []);
  return allDevices.filter(d => directIds.has(d.id) || (d.groupId != null && groupIds.has(d.groupId)));
};

export const userGroupsFromMap = (userId: number, allGroups: Group[], map: PermissionsMap): Group[] => {
  const ids = new Set(map.groupsByUser[userId] ?? []);
  return allGroups.filter(g => ids.has(g.id));
};

export const fetchUsersForDevice = async (deviceId: number): Promise<User[]> => {
  return callApi("users", { deviceId: deviceId.toString() });
};

/**
 * Builds a deviceId → User[] map from the permissions map and device list.
 * Single /api/permissions call instead of M per-user requests.
 */
export const fetchDeviceUsersMap = async (users: User[], allDevices: Device[]): Promise<Record<number, User[]>> => {
  const perms = await fetchPermissionsMap();
  const map: Record<number, User[]> = {};
  for (const user of users) {
    const devices = userDevicesFromMap(user.id, allDevices, perms);
    for (const d of devices) {
      if (!map[d.id]) map[d.id] = [];
      map[d.id].push(user);
    }
  }
  return map;
};


export const linkDevice = async (userId: number, deviceId: number): Promise<void> => {
  const response = await fetch(`${EDGE_FUNCTION_URL}/api/permissions`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    },
    body: JSON.stringify({ userId, deviceId }),
  });

  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.error || 'Failed to link device');
  }
};

export const unlinkDevice = async (userId: number, deviceId: number): Promise<void> => {
  const response = await fetch(`${EDGE_FUNCTION_URL}/api/permissions`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    },
    body: JSON.stringify({ userId, deviceId }),
  });

  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.error || 'Failed to unlink device');
  }
};

export const linkGroup = async (userId: number, groupId: number): Promise<void> => {
  const response = await fetch(`${EDGE_FUNCTION_URL}/api/permissions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
    body: JSON.stringify({ userId, groupId }),
  });
  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.error || 'Failed to link group');
  }
};

export const unlinkGroup = async (userId: number, groupId: number): Promise<void> => {
  const response = await fetch(`${EDGE_FUNCTION_URL}/api/permissions`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
    body: JSON.stringify({ userId, groupId }),
  });
  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.error || 'Failed to unlink group');
  }
};

export const fetchManagedUsers = async (userId: number): Promise<User[]> => {
  return callApi("users", { userId: userId.toString() });
};

export const linkManagedUser = async (userId: number, managedUserId: number): Promise<void> => {
  const response = await fetch(`${EDGE_FUNCTION_URL}/api/permissions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
    body: JSON.stringify({ userId, managedUserId }),
  });
  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.error || 'Failed to link user');
  }
};

export const unlinkManagedUser = async (userId: number, managedUserId: number): Promise<void> => {
  const response = await fetch(`${EDGE_FUNCTION_URL}/api/permissions`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
    body: JSON.stringify({ userId, managedUserId }),
  });
  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.error || 'Failed to unlink user');
  }
};

export const fetchDevicesForGeofence = async (geofenceId: number): Promise<Device[]> => {
  return callApi("devices", { geofenceId: geofenceId.toString() });
};

export const linkGeofenceToDevice = async (deviceId: number, geofenceId: number): Promise<void> => {
  const response = await fetch(`${EDGE_FUNCTION_URL}/api/permissions`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    },
    body: JSON.stringify({ deviceId, geofenceId }),
  });

  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.error || 'Failed to link geofence to device');
  }
};

export const unlinkGeofenceFromDevice = async (deviceId: number, geofenceId: number): Promise<void> => {
  const response = await fetch(`${EDGE_FUNCTION_URL}/api/permissions`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    },
    body: JSON.stringify({ deviceId, geofenceId }),
  });

  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.error || 'Failed to unlink geofence from device');
  }
};

export interface Command {
  deviceId: number;
  type: string;
  textChannel?: boolean;
  attributes?: Record<string, unknown>;
}

export interface CommandType {
  type: string;
}

export const fetchCommandTypes = async (deviceId: number): Promise<CommandType[]> => {
  const response = await callApi(`commands/types?deviceId=${deviceId}`);
  return response as CommandType[];
};

export const sendCommand = async (command: Command): Promise<void> => {
  const response = await fetch(`${EDGE_FUNCTION_URL}/api/commands/send`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    },
    body: JSON.stringify(command),
  });

  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.error || 'Failed to send command');
  }
};

export const sendGeofenceConfig = async (deviceId: number, _geofence: Geofence): Promise<void> => {
  await sendCommand({ deviceId, type: 'getVersion' });
};

// Drivers
export interface Driver {
  id: number;
  name: string;
  uniqueId: string;
  attributes: Record<string, unknown>;
}

export const fetchDrivers = async (): Promise<Driver[]> => {
  return callApi("drivers");
};

export const createDriver = async (driver: Omit<Driver, 'id'>): Promise<Driver> => {
  const response = await fetch(`${EDGE_FUNCTION_URL}/api/drivers`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    },
    body: JSON.stringify(driver),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(errorText.slice(0, MAX_ERROR_LENGTH) || 'Failed to create driver');
  }
  return response.json();
};

export const updateDriver = async (driver: Driver): Promise<Driver> => {
  const response = await fetch(`${EDGE_FUNCTION_URL}/api/drivers/${driver.id}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    },
    body: JSON.stringify(driver),
  });

  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to update driver');
  return data;
};

export const deleteDriver = async (id: number): Promise<void> => {
  const response = await fetch(`${EDGE_FUNCTION_URL}/api/drivers/${id}`, {
    method: 'DELETE',
    headers: {
      'Accept': 'application/json',
    },
  });

  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.error || 'Failed to delete driver');
  }
};

// Notifications
export interface NotificationRule {
  id: number;
  type: string;
  always: boolean;
  notificators: string;
  calendarId: number;
  attributes: Record<string, unknown>;
}

export interface NotificationType {
  type: string;
}

export interface Notificator {
  type: string;
}

export const fetchNotifications = (): Promise<NotificationRule[]> => callApi('notifications');
export const fetchNotificationTypes = (): Promise<NotificationType[]> => callApi('notifications/types');
export const fetchNotificators = (): Promise<Notificator[]> => callApi('notifications/notificators');

export const createNotification = async (rule: Omit<NotificationRule, 'id'>): Promise<NotificationRule> => {
  const response = await fetch(`${EDGE_FUNCTION_URL}/api/notifications`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    },
    body: JSON.stringify(rule),
  });

  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to create notification');
  return data;
};

export const updateNotification = async (rule: NotificationRule): Promise<NotificationRule> => {
  const response = await fetch(`${EDGE_FUNCTION_URL}/api/notifications/${rule.id}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    },
    body: JSON.stringify(rule),
  });

  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to update notification');
  return data;
};

export const deleteNotification = async (id: number): Promise<void> => {
  const response = await fetch(`${EDGE_FUNCTION_URL}/api/notifications/${id}`, {
    method: 'DELETE',
    headers: {
      'Accept': 'application/json',
    },
  });

  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.error || 'Failed to delete notification');
  }
};
